<div class="flex flex-col gap-3 p-1 h-full">
  <!-- District Filter -->
  <div class="flex items-center gap-3">
    <div
      class="flex items-center gap-2.5 rounded-xl px-4 py-2 shadow-sm transition-colors"
      [ngClass]="selectedDistrict() !== 'all districts'
        ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700'
        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'"
    >
      <svg
        class="w-5 h-5 text-blue-500 dark:text-blue-400 shrink-0"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="1.5"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
        />
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
        />
      </svg>
      <span
        class="text-sm font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap"
        >Filter by District</span
      >
      <app-dropdown
        class="min-w-80 z-30"
        [options]="districtDropdownOptions()"
        [keyField]="'key'"
        [labelField]="'value'"
        [outputField]="'key'"
        [dropdownPlacement]="'bottom'"
        [(selectedKey)]="selectedDistrict"
      />
    </div>
  </div>

  <!-- Top Row: Stats + Doughnut + Line Chart (3 columns) -->
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-3 top-row overflow-hidden">
    <!-- Stats Cards (left column - 2x2 grid) -->
    <div class="lg:col-span-3 grid grid-cols-2 grid-rows-2 gap-3">
      @for (card of adminCardData(); track card.title; let i = $index) {
      <div
        class="relative flex flex-col items-center justify-center text-center bg-white rounded-xl shadow-sm border border-gray-100 border-l-[3px] dark:border-gray-700 dark:bg-gray-800 px-3 pt-8 pb-3 transition-all hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600"
        [ngClass]="card.accentBorder"
      >
        <!-- Icon (top-left) -->
        <div
          class="absolute top-2.5 left-2.5 flex items-center justify-center w-8 h-8 rounded-lg"
          [ngClass]="card.iconBg"
        >
          <div [innerHTML]="card.icon"></div>
        </div>
        <!-- Info tooltip (top-right, only for Active/Total Growers) -->
        @if (i === 0) {
        <div class="absolute top-2.5 right-2.5 group/info">
          <svg
            class="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
            />
          </svg>
          <div
            class="invisible group-hover/info:visible absolute right-0 top-full mt-1 px-2.5 py-1.5 text-xs text-white bg-gray-800 dark:bg-gray-600 rounded-md shadow-lg whitespace-nowrap z-10"
          >
            <span class="font-semibold">{{ card.value | number }}</span> growers
            claimed fields<br />
            <span class="font-semibold"
              >{{ card.secondaryValue | number }}</span
            >
            total growers registered
          </div>
        </div>
        }
        <span
          class="text-[13px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 truncate w-full"
        >
          {{card.title}}
        </span>
        @if (card.secondaryValue !== undefined) {
        <span
          class="text-3xl font-bold text-gray-900 dark:text-white leading-tight"
        >
          {{card.value | number}}<span
            class="text-gray-400 dark:text-gray-500 mx-0.5"
            >/</span
          >{{card.secondaryValue | number}}
        </span>
        } @else {
        <span
          class="text-3xl font-bold text-gray-900 dark:text-white leading-tight"
        >
          {{card.value | number}}
        </span>
        }
      </div>
      }
    </div>

    <!-- Crop type distribution doughnut (center column) -->
    <div
      class="lg:col-span-4 flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 dark:bg-gray-800 p-4"
    >
      <div class="flex items-center gap-2 mb-1">
        <svg
          class="w-5 h-5 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z"
          />
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z"
          />
        </svg>
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          Crop Type Distribution
        </h2>
      </div>
      <!-- Chart + scrollable legend side by side -->
      <div class="flex flex-1 min-h-0 gap-2">
        <div class="flex-[3] min-w-0 min-h-0 relative">
          <canvas
            #pieChartCanvas
            baseChart
            type="doughnut"
            [data]="pieChartData()"
            [options]="pieChartOptions()"
            [plugins]="pieChartPlugins"
          >
          </canvas>
        </div>
        <!-- Custom scrollable legend -->
        <div class="flex-[2] overflow-y-auto pie-legend-scroll min-h-0 py-1">
          @for (item of pieLegendItems(); track item.label; let i = $index) {
          <div
            class="flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-colors"
            [class.bg-gray-100]="hoveredPieLegendIndex() === i"
            [class.dark:bg-gray-700]="hoveredPieLegendIndex() === i"
            (mouseenter)="onPieLegendHover(i)"
            (mouseleave)="onPieLegendLeave()"
          >
            <span
              class="w-2.5 h-2.5 rounded-sm shrink-0"
              [style.background-color]="item.color"
            ></span>
            <span class="text-xs text-gray-700 dark:text-gray-300 truncate"
              >{{ item.label }}</span
            >
            <span
              class="text-[11px] text-gray-400 dark:text-gray-500 ml-auto shrink-0"
              >{{ item.value | number:'1.1-1' }}%</span
            >
          </div>
          }
        </div>
      </div>
    </div>

    <!-- Activity Trend (right column) -->
    <div
      class="lg:col-span-5 flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 dark:bg-gray-800 p-4"
    >
      <div class="flex items-center justify-between mb-1">
        <div class="flex items-center gap-2">
          <svg
            class="w-5 h-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
            />
          </svg>
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Activity Trends
          </h2>
          <span class="text-xs font-medium text-gray-400 dark:text-gray-500"
            >Last 30 Days</span
          >
          <!-- Inline legend -->
          <div class="flex items-center gap-3 ml-2">
            <div class="flex items-center gap-1">
              <span class="w-3 h-0.5 rounded-full bg-[#3B82F6]"></span>
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Logins</span
              >
            </div>
            @if (lineChartSelectedOption() !== 'entities') {
            <div class="flex items-center gap-1">
              <span class="w-3 h-0.5 rounded-full bg-[#10B981]"></span>
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Claims</span
              >
            </div>
            } @if (lineChartSelectedOption() !== 'entities' &&
            lineChartSelectedOption() !== 'managers') {
            <div class="flex items-center gap-1">
              <span class="w-3 h-0.5 rounded-full bg-[#F59E0B]"></span>
              <span class="text-sm text-gray-500 dark:text-gray-400"
                >Updates</span
              >
            </div>
            }
          </div>
        </div>
        <app-dropdown
          class="min-w-36 z-20"
          [options]="lineChartDropdownOptions"
          [keyField]="'key'"
          [labelField]="'value'"
          [outputField]="'key'"
          [dropdownPlacement]="'bottom'"
          [(selectedKey)]="lineChartSelectedOption"
        />
      </div>
      <div class="chart-container chart-container--line">
        <canvas
          baseChart
          type="line"
          [data]="lineChartData()"
          [options]="lineChartOptions()"
        >
        </canvas>
      </div>
    </div>
  </div>

  <!-- Fields by District -->
  <div
    class="flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 dark:bg-gray-800 p-4 @container flex-[3] min-h-0"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <svg
          class="w-5 h-5 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"
          />
        </svg>
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
          Fields by District
        </h2>
        <!-- Inline legend -->
        <div class="flex items-center gap-4 ml-4">
          <div class="flex items-center gap-1.5">
            <span class="w-3 h-3 rounded-sm bg-[#3B82F6]"></span>
            <span class="text-sm text-gray-500 dark:text-gray-400"
              >Total Fields</span
            >
          </div>
          <div class="flex items-center gap-1.5">
            <span class="w-3 h-3 rounded-sm bg-[#93C5FD]"></span>
            <span class="text-sm text-gray-500 dark:text-gray-400"
              >Claimed Fields</span
            >
          </div>
          <div class="flex items-center gap-1.5">
            <span
              class="w-3 h-3 rounded-full border-2 border-[#F59E0B] bg-transparent"
            ></span>
            <span class="text-sm text-gray-500 dark:text-gray-400"
              >Claim Rate (%)</span
            >
          </div>
        </div>
      </div>
      <app-dropdown
        class="min-w-36 z-20"
        [options]="comboChartDropdownOptions"
        [keyField]="'key'"
        [labelField]="'value'"
        [outputField]="'key'"
        [(selectedKey)]="comboChartDropdownSelection"
        [dropdownPlacement]="'bottom'"
      />
    </div>
    @if(comboChartDropdownSelection() === 'combo'){
    <div
      #comboChartContainer
      class="chart-container chart-container--combo mt-1 flex-1 min-h-0"
    >
      @if (isSmallComboContainer()) {
      <canvas
        baseChart
        type="bar"
        [data]="$any(comboChartData())"
        [options]="comboChartOptions()"
        [plugins]="comboChartPlugins"
      >
      </canvas>
      } @else {
      <canvas
        baseChart
        type="bar"
        [data]="$any(comboChartData())"
        [options]="comboChartOptions()"
        [plugins]="comboChartPlugins"
      >
      </canvas>
      }
    </div>
    } @else {
    <!-- Table -->
    <div
      class="overflow-auto mt-2 rounded-lg border border-gray-200 dark:border-gray-700 flex-1 min-h-0"
    >
      <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
        <thead
          class="text-xs font-semibold text-gray-500 uppercase bg-gray-50/80 dark:bg-gray-700/50 dark:text-gray-400 sticky top-0"
        >
          <tr>
            <th class="px-4 py-3">District</th>
            <th class="px-4 py-3 text-right">Total Fields</th>
            <th class="px-4 py-3 text-right">Claimed Fields</th>
            <th class="px-4 py-3 text-right">Claim Rate</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
          @for (row of districtTableData(); track row.name) {
          <tr
            class="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
          >
            <td
              class="px-4 py-2.5 font-medium text-gray-900 dark:text-white whitespace-nowrap"
            >
              {{ row.name }}
            </td>
            <td class="px-4 py-2.5 text-right">
              <span
                class="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium"
              >
                {{ row.total | number }}
              </span>
            </td>
            <td class="px-4 py-2.5 text-right">
              <span
                class="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium"
              >
                {{ row.claimed | number }}
              </span>
            </td>
            <td class="px-4 py-2.5 text-right">
              <span
                class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
                [ngClass]="{
                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400': row.claimRate >= 75,
                  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400': row.claimRate >= 40 && row.claimRate < 75,
                  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400': row.claimRate < 40
                }"
              >
                {{ row.claimRate | number:'1.1-1' }}%
              </span>
            </td>
          </tr>
          }
        </tbody>
        <tfoot class="bg-gray-50/80 dark:bg-gray-700/50">
          <tr>
            <td
              class="px-4 py-3 text-xs font-semibold uppercase text-gray-600 dark:text-gray-400 whitespace-nowrap"
            >
              Total
            </td>
            <td
              class="px-4 py-3 text-right text-sm font-bold text-blue-600 dark:text-blue-400"
            >
              {{ districtTotals().total | number }}
            </td>
            <td
              class="px-4 py-3 text-right text-sm font-bold text-green-600 dark:text-green-400"
            >
              {{ districtTotals().claimed | number }}
            </td>
            <td class="px-4 py-3 text-right">
              <span
                class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
                [ngClass]="{
                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400': districtTotals().claimRate >= 75,
                  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400': districtTotals().claimRate >= 40 && districtTotals().claimRate < 75,
                  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400': districtTotals().claimRate < 40
                }"
              >
                {{ districtTotals().claimRate | number:'1.1-1' }}%
              </span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    }
  </div>
</div>

<ng-template #table> </ng-template>
